import { useEffect, useState } from "react";
import { useParams, Link } from "wouter";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import logoImage from "@assets/favicon new no background cc6633.png";
import { useAuth } from "@/hooks/useAuth";
import { getCsrfHeaders } from "@/lib/queryClient";

type LookupState =
  | { status: "loading" }
  | { status: "not_found" }
  | { status: "error" }
  | { status: "found"; firstName: string | null; userType: string | null; company: string | null };

type ConnectState =
  | { status: "idle" }
  | { status: "connecting" }
  | { status: "done"; partnered: boolean; alreadyPartners: boolean; reason?: string }
  | { status: "error"; message: string };

export default function JoinPage() {
  const { code } = useParams<{ code: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const isAuthenticated = !!user;

  const [lookup, setLookup] = useState<LookupState>({ status: "loading" });
  const [connect, setConnect] = useState<ConnectState>({ status: "idle" });

  // 1. Resolve the referral code on mount
  useEffect(() => {
    if (!code) {
      setLookup({ status: "not_found" });
      return;
    }
    fetch(`/api/referral/${encodeURIComponent(code)}`)
      .then(async (res) => {
        if (res.status === 404) {
          setLookup({ status: "not_found" });
          return;
        }
        if (!res.ok) {
          setLookup({ status: "error" });
          return;
        }
        const data = await res.json();
        setLookup({
          status: "found",
          firstName: data.firstName ?? null,
          userType: data.userType ?? null,
          company: data.company ?? null,
        });
      })
      .catch(() => setLookup({ status: "error" }));
  }, [code]);

  // 2. If authenticated and lookup succeeded, auto-connect
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    if (lookup.status !== "found") return;
    if (connect.status !== "idle") return;

    setConnect({ status: "connecting" });
    fetch(`/api/referral/${encodeURIComponent(code!)}/connect`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
      credentials: "include",
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          setConnect({ status: "error", message: data.error || "Something went wrong." });
          return;
        }
        setConnect({
          status: "done",
          partnered: data.partnered ?? false,
          alreadyPartners: data.alreadyPartners ?? false,
          reason: data.reason ?? undefined,
        });
      })
      .catch(() =>
        setConnect({ status: "error", message: "Something went wrong. Please try again." })
      );
  }, [authLoading, isAuthenticated, lookup.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const roleLabel =
    lookup.status === "found"
      ? lookup.userType === "Shipper"
        ? "Shipper"
        : lookup.userType === "Broker"
        ? "Broker"
        : (lookup.userType ?? "member")
      : "";

  const inviterName =
    lookup.status === "found" && lookup.firstName
      ? lookup.company
        ? `${lookup.firstName} at ${lookup.company}`
        : lookup.firstName
      : null;

  const inviterSentence =
    lookup.status === "found"
      ? inviterName
        ? `${inviterName}, a ${roleLabel} on Fill The Trucks, invited you to connect as a partner. Sign up and you'll be automatically connected as partners — no extra steps needed. Once connected, you'll be able to bid directly on any loads ${lookup.firstName} invites you to.`
        : "A member of Fill The Trucks invited you to connect as a partner."
      : "";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-6 pb-2">
          <Link href="/" className="flex justify-center" data-testid="link-join-logo">
            <img src={logoImage} alt="Fill The Trucks" className="h-24" />
          </Link>
        </CardHeader>

        <CardContent className="space-y-4 pt-2">
          {/* ── Lookup: loading ────────────────────────────────────────────── */}
          {lookup.status === "loading" && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* ── Lookup: not found ──────────────────────────────────────────── */}
          {lookup.status === "not_found" && (
            <div className="text-center space-y-4 py-4">
              <AlertCircle className="w-8 h-8 text-muted-foreground mx-auto" />
              <p className="text-muted-foreground">This invite link isn't valid.</p>
              <Button asChild variant="outline" data-testid="button-join-register-fallback">
                <Link href="/register">Create an account</Link>
              </Button>
            </div>
          )}

          {/* ── Lookup: error ─────────────────────────────────────────────── */}
          {lookup.status === "error" && (
            <div className="text-center space-y-4 py-4">
              <AlertCircle className="w-8 h-8 text-muted-foreground mx-auto" />
              <p className="text-muted-foreground">
                Something went wrong loading this invite. Please try again later.
              </p>
            </div>
          )}

          {/* ── Lookup: found ─────────────────────────────────────────────── */}
          {lookup.status === "found" && (
            <div className="space-y-5">
              <div className="text-center space-y-2">
                <h1 className="text-2xl font-bold">You've been invited</h1>
                <p className="text-muted-foreground text-sm">{inviterSentence}</p>
              </div>

              {/* Auth state still resolving */}
              {authLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              )}

              {/* ── Not authenticated ──────────────────────────────────────── */}
              {!authLoading && !isAuthenticated && (
                <div className="space-y-3">
                  <Button
                    className="w-full"
                    asChild
                    data-testid="button-join-signup"
                  >
                    <Link href={`/register?ref=${encodeURIComponent(code!)}`}>
                      Sign up to connect
                    </Link>
                  </Button>
                  <p className="text-center text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <Link
                      href={`/login?returnTo=${encodeURIComponent("/join/" + code!)}`}
                      className="text-primary hover:underline font-medium"
                      data-testid="link-join-login"
                    >
                      Log in to connect instantly.
                    </Link>
                  </p>
                </div>
              )}

              {/* ── Authenticated — connect in flight ─────────────────────── */}
              {!authLoading && isAuthenticated && connect.status === "connecting" && (
                <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Connecting…
                </div>
              )}

              {/* ── Connected: success ────────────────────────────────────── */}
              {!authLoading && isAuthenticated && connect.status === "done" && connect.partnered && !connect.alreadyPartners && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    You're now connected as partners.
                  </div>
                  <Button className="w-full" asChild data-testid="button-join-dashboard">
                    <Link href="/dashboard">Go to dashboard</Link>
                  </Button>
                </div>
              )}

              {/* ── Connected: already partners ───────────────────────────── */}
              {!authLoading && isAuthenticated && connect.status === "done" && connect.alreadyPartners && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    You're already connected as partners.
                  </div>
                  <Button className="w-full" asChild data-testid="button-join-dashboard">
                    <Link href="/dashboard">Go to dashboard</Link>
                  </Button>
                </div>
              )}

              {/* ── Connected: same role ──────────────────────────────────── */}
              {!authLoading && isAuthenticated && connect.status === "done" && !connect.partnered && connect.reason === "SAME_ROLE" && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Welcome! Since you're both {roleLabel}s, no partner connection was needed, but
                    you're all set.
                  </p>
                  <Button className="w-full" asChild data-testid="button-join-dashboard">
                    <Link href="/dashboard">Go to dashboard</Link>
                  </Button>
                </div>
              )}

              {/* ── Connect: error ────────────────────────────────────────── */}
              {!authLoading && isAuthenticated && connect.status === "error" && (
                <div className="space-y-4">
                  <div className="flex items-start gap-2 text-sm text-muted-foreground">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {connect.message}
                  </div>
                  <Button variant="outline" className="w-full" asChild data-testid="button-join-dashboard">
                    <Link href="/dashboard">Go to dashboard</Link>
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
